import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Volume2, VolumeX } from "lucide-react";
import Logo from "./Logo";
import { InfinityLoop } from "./Illustrations";
import { useSound } from "./SoundContext";

export default function Footer() {
  const { enabled, toggle } = useSound();
  return (
    <footer
      data-testid="site-footer"
      className="bg-stone text-cream relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h2 className="font-headings text-4xl md:text-5xl tracking-tight leading-tight">
              Let's <span className="text-mint">unite</span> over a conversation.
            </h2>
            <p className="mt-6 text-cream/75 max-w-md leading-relaxed font-body">
              Whether you're looking for your next role or your next hire, we'd
              love to hear from you.
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="inline-flex items-center gap-2 mt-8 bg-mint text-ink hover:bg-mint-deep px-7 py-3.5 rounded-full font-body font-medium text-sm transition-transform hover:-translate-y-0.5"
            >
              Start a conversation →
            </Link>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <p className="text-xs uppercase tracking-[0.25em] text-mint font-medium mb-5">
              Navigate
            </p>
            <ul className="space-y-3 font-body text-cream/85">
              <li><Link to="/" className="link-underline" data-testid="footer-link-home">Home</Link></li>
              <li><Link to="/about" className="link-underline" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/find-a-job" className="link-underline" data-testid="footer-link-find-job">Find a Job</Link></li>
              <li><Link to="/find-a-candidate" className="link-underline" data-testid="footer-link-find-candidate">Find a Candidate</Link></li>
              <li><Link to="/help" className="link-underline" data-testid="footer-link-help">Help</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-mint font-medium mb-5">
              Contact
            </p>
            <ul className="space-y-4 font-body text-cream/85">
              <li className="flex items-start gap-3" data-testid="footer-phone">
                <Phone size={16} className="mt-1 flex-shrink-0 text-mint" />
                <a href="tel:+64274382028" className="hover:text-mint">0274 382 028</a>
              </li>
              <li className="flex items-start gap-3" data-testid="footer-email">
                <Mail size={16} className="mt-1 flex-shrink-0 text-mint" />
                <a href="mailto:Bronwyn@uniterecruitment.co.nz" className="hover:text-mint break-all">
                  Bronwyn@uniterecruitment.co.nz
                </a>
              </li>
              <li className="flex items-start gap-3" data-testid="footer-address">
                <MapPin size={16} className="mt-1 flex-shrink-0 text-mint" />
                <span>Aotearoa, New Zealand</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Decorative infinity line */}
        <div className="mt-16 mb-10 opacity-50">
          <InfinityLoop className="w-full h-20 md:h-28" stroke="#C2FFE1" strokeWidth={4} />
        </div>

        <div className="pt-6 border-t border-cream/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo size="sm" className="text-mint" />
            <p className="text-xs text-cream/55 font-body">
              © {new Date().getFullYear()} Unite Recruitment. All rights reserved.
            </p>
          </div>
          <div className="flex items-center gap-5 text-xs font-body text-cream/55">
            <button
              type="button"
              onClick={toggle}
              data-testid="footer-sound-toggle"
              aria-pressed={enabled}
              aria-label={enabled ? "Mute transition sound" : "Enable transition sound"}
              className="inline-flex items-center gap-2 hover:text-mint transition-colors group"
            >
              {enabled ? (
                <Volume2 size={14} className="text-mint" strokeWidth={1.5} />
              ) : (
                <VolumeX size={14} strokeWidth={1.5} />
              )}
              <span className="uppercase tracking-[0.2em]">
                Sound {enabled ? "on" : "off"}
              </span>
            </button>
            <Link to="/terms" className="hover:text-mint" data-testid="footer-link-terms">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-mint" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/brand" className="hover:text-mint" data-testid="footer-link-brand">Brand</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
